import { Inject, Injectable, Optional } from '@angular/core';

import { RUN_MODE_TOKEN, RunMode } from 'src/environments/runtime';

import {
  CLOUD_HOST,
  FRODO_API_HOST,
  GANDALF_HOST,
  GANDALF_SEND_MESSAGE_HOST,
  GATEWAY_SERVICES,
  HELMSDEEP_HOST,
} from '../../utils/constants';

@Injectable({
  providedIn: 'root',
})
export class EndpointService {
  private _nodeId = '';

  constructor(
    @Inject('Window') private window: Window,
    @Optional() @Inject(RUN_MODE_TOKEN) private runMode: RunMode | null
  ) {
    const urlNodeId = this.extractNodeIdFromUrl(this.window.location.search);

    if (urlNodeId) {
      if (this.window.sessionStorage) {
        this.window.sessionStorage.setItem('deviceId', urlNodeId);
      }
      this._nodeId = urlNodeId;
    } else {
      this._nodeId =
        (this.window.sessionStorage && this.window.sessionStorage.getItem('deviceId')) || '';
    }
  }

  private extractNodeIdFromUrl(search: string): string {
    const isNodeOrDeviceIdKey = (key: string): boolean => key === 'nodeid' || key === 'deviceid';
    const match = Array.from(new URLSearchParams(search)).find(
      ([key, value]) => isNodeOrDeviceIdKey(key.toLowerCase()) && value
    );

    return match?.[1] ?? '';
  }

  get nodeId() {
    return this._nodeId;
  }

  /**
   * Gateway routing (`/gateway/<service>`) is only used for the `cloud`
   * employee-UI deployment, which is a shared pod fronted by a gateway that
   * routes by an explicit `deviceId` query/header instead of a URL path
   * segment. `remote-pickup` and `device` deployments keep using the legacy
   * nodeId-in-path pattern (`${CLOUD_HOST}/${nodeId}/<service>`), which
   * matches their existing ingress configuration.
   */
  private isGatewayMode(): boolean {
    return this.runMode === 'cloud' && !!this._nodeId;
  }

  getHelmsdeepUrl(): string {
    if (this.isGatewayMode()) {
      return `${CLOUD_HOST}/gateway/${GATEWAY_SERVICES.HELMSDEEP}`;
    }
    if (this._nodeId) {
      return `${CLOUD_HOST}/${this._nodeId}/helmsdeep`;
    }
    return HELMSDEEP_HOST;
  }

  getGandalfUrl(): string {
    if (this.isGatewayMode()) {
      return `${CLOUD_HOST}/gateway/${GATEWAY_SERVICES.GANDALF}`;
    }
    if (this._nodeId) {
      return `${CLOUD_HOST}/${this._nodeId}/gandalf`;
    }
    return GANDALF_HOST;
  }

  getBabelfishUrl(): string {
    if (this.isGatewayMode()) {
      return `${CLOUD_HOST}/gateway/${GATEWAY_SERVICES.BABELFISH}`;
    }
    if (this._nodeId) {
      return `${CLOUD_HOST}/${this._nodeId}/babelfish`;
    }
    return `${GANDALF_SEND_MESSAGE_HOST}/babelfish`;
  }

  getFrodoAPIUrl(): string {
    if (this.isGatewayMode()) {
      return `${CLOUD_HOST}/gateway/${GATEWAY_SERVICES.FRODO_API}`;
    }
    if (this._nodeId) {
      return `${CLOUD_HOST}/api/${this._nodeId}`;
    }
    return FRODO_API_HOST;
  }
}
